/**
 * Supplier delivery choice (sections/supplier-delivery-choice.liquid +
 * snippets/supplier-delivery-choice.liquid), cart page.
 *
 *  - Copies the section's box into <cart-items> (after the item table) and re-fetches the
 *    section whenever the theme re-renders the cart (our copy disappearing is the signal).
 *  - Saves the customer's pick as the cart attribute "Delivery choice".
 *  - Keeps the "Separate delivery" fee line in step with data-fee-needed: exactly one when
 *    needed, none otherwise (a cart that stops being mixed loses the fee).
 *
 * The radio buttons end up inside <cart-items>, whose own 'change' listener treats any change
 * as a quantity update, so their change events are caught on the way down and stopped there.
 */
(() => {
  if (window.supplierDeliveryChoice) return;

  const root = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
  const headers = { 'Content-Type': 'application/json', Accept: 'application/json' };
  let busy = false;
  let lastActed = null;
  let seq = 0;
  let opsStart = 0;
  let ops = 0;

  const source = () => document.querySelector('[data-sdc-source]');
  const box = () => document.querySelector('cart-items .sdc-host [data-sdc]');

  async function post(url, body) {
    const res = await fetch(`${root}${url}`, { method: 'POST', headers, body: JSON.stringify(body) });
    if (!res.ok) throw new Error(`${url} ${res.status}`);
    return res.json();
  }

  function refreshCart() {
    const cartItems = document.querySelector('cart-items');
    if (cartItems && typeof cartItems.refresh === 'function' && cartItems.dataset.empty === 'false') {
      cartItems.refresh();
    } else {
      window.location.reload();
    }
  }

  /** Puts a copy of the box into each non-empty cart, replacing any older copy. */
  function insert() {
    const src = source();
    if (!src) return;
    document.querySelectorAll('cart-items').forEach((cartItems) => {
      const old = cartItems.querySelector(':scope > .sdc-host');
      if (old) old.remove();
      if (cartItems.dataset.empty === 'true' || !cartItems.querySelector('.cart-item')) return;
      const host = document.createElement('div');
      host.className = 'sdc-host';
      host.innerHTML = src.innerHTML;
      const after = cartItems.querySelector(':scope > .fgo-panel') || cartItems.querySelector(':scope > table');
      if (after) after.after(host);
      else cartItems.append(host);
    });
  }

  async function refresh() {
    const src = source();
    if (!src || busy) return;
    const id = src.dataset.sectionId;
    const mine = ++seq;
    try {
      // No Accept header: with one, OZBC's storefront returned an empty section.
      const res = await fetch(`${window.location.pathname}?sections=${encodeURIComponent(id)}`);
      if (!res.ok) throw new Error(res.status);
      const sections = await res.json();
      if (mine !== seq || !sections[id]) return;
      const tmpl = document.createElement('template');
      tmpl.innerHTML = sections[id];
      const fresh = tmpl.content.querySelector('[data-sdc-source]');
      if (fresh) src.innerHTML = fresh.innerHTML;
    } catch (e) {
      console.warn('Supplier delivery choice: refresh failed', e); // eslint-disable-line
      return;
    }
    insert();
    reconcile();
  }

  function tooMany() {
    const now = Date.now();
    if (now - opsStart > 30000) { opsStart = now; ops = 0; }
    ops += 1;
    return ops > 6;
  }

  function setBusy(on, message) {
    const b = box();
    if (!b) return;
    b.classList.toggle('is-busy', on);
    const status = b.querySelector('[data-sdc-status]');
    if (status) status.textContent = message || '';
  }

  /** Brings the fee line into line with data-fee-needed. */
  async function reconcile() {
    const b = box() || (source() && source().querySelector('[data-sdc]'));
    if (!b || busy) return;
    const needed = b.dataset.feeNeeded === 'true';
    const qty = Number(b.dataset.feeQty || 0);
    const keys = (b.dataset.feeKeys || '').split(',').filter(Boolean);
    const state = `${needed}|${qty}|${keys.join(',')}`;
    if ((needed && qty === 1) || (!needed && qty === 0) || state === lastActed) return;
    if (needed && !b.dataset.feeVariant) return;
    if (tooMany()) {
      console.warn('Supplier delivery choice: too many cart changes, pausing'); // eslint-disable-line
      return;
    }
    lastActed = state;
    busy = true;
    setBusy(true, 'Updating your cart…');
    try {
      if (needed && qty === 0) {
        await post('cart/add.js', { items: [{ id: Number(b.dataset.feeVariant), quantity: 1 }] });
      } else {
        for (let i = 0; i < keys.length; i += 1) {
          await post('cart/change.js', { id: keys[i], quantity: needed && i === 0 ? 1 : 0 }); // eslint-disable-line no-await-in-loop
        }
      }
    } catch (e) {
      console.warn('Supplier delivery choice:', e); // eslint-disable-line
    } finally {
      busy = false;
      refreshCart();
    }
  }

  async function choose(value) {
    busy = true;
    setBusy(true, 'Saving your choice…');
    try {
      await post('cart/update.js', { attributes: { 'Delivery choice': value } });
    } catch (e) {
      console.warn('Supplier delivery choice: could not save', e); // eslint-disable-line
      busy = false;
      setBusy(false, 'Your choice could not be saved. Please try again.');
      return;
    }
    busy = false;
    lastActed = null;
    refreshCart();
  }

  document.addEventListener('change', (evt) => {
    const radio = evt.target.closest && evt.target.closest('[data-sdc-choice]');
    if (!radio) return;
    evt.stopPropagation();
    if (radio.checked) choose(radio.value);
  }, true);

  function cartLostBox() {
    return Array.from(document.querySelectorAll('cart-items')).some((c) => c.dataset.empty !== 'true'
      && c.querySelector('.cart-item') && !c.querySelector(':scope > .sdc-host'));
  }

  let timer;
  new MutationObserver(() => {
    clearTimeout(timer);
    timer = setTimeout(() => { if (cartLostBox()) refresh(); }, 150);
  }).observe(document.body, { childList: true, subtree: true });

  function start() {
    insert();
    reconcile();
  }

  window.supplierDeliveryChoice = { refresh, reconcile };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();
})();
